/**
 * Switch between master and sub-accounts (ys142857 wallet / 子账户).
 */
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { subAccountsAPI } from '../api/sub-accounts';
import { useAuthStore } from '../store/auth';
import { useActiveAccountStore } from '../store/activeAccount';
import { ModalSheet } from './ui/ModalSheet';
import { THEMED_SURFACE_COLORS as C, ThemedChip } from './ui/ThemedSurface';

type SubRow = {
  id: number;
  username: string;
  level_code?: string;
  balances?: Record<string, string>;
  uo?: string;
  ep1?: string;
  sp1?: string;
};

function fmt(v: string | undefined): string {
  const n = Number(v ?? '0');
  if (!Number.isFinite(n)) return v ?? '0';
  return n.toLocaleString(undefined, { maximumFractionDigits: 4 });
}

function AccountOption({
  label,
  sublabel,
  active,
  onClick,
}: {
  label: string;
  sublabel?: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center justify-between rounded-xl border px-4 py-3 text-left text-sm transition-all active:scale-[0.98]"
      style={{
        borderColor: active ? `${C.gold}66` : C.softBorder,
        background: active ? C.chipBg : C.inputBg,
        color: C.text,
      }}
    >
      <div className="min-w-0">
        <div className="truncate font-semibold">{label}</div>
        {sublabel ? (
          <div className="mt-1 truncate text-xs" style={{ color: C.muted }}>
            {sublabel}
          </div>
        ) : null}
      </div>
      {active ? <ThemedChip active>✓</ThemedChip> : null}
    </button>
  );
}

export function AccountSwitcher({
  open,
  onClose,
  subCount,
}: {
  open: boolean;
  onClose: () => void;
  subCount?: number;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const profile = useAuthStore((s) => s.profile);
  const activeId = useActiveAccountStore((s) => s.accountId);
  const setActiveAccount = useActiveAccountStore((s) => s.setActiveAccount);

  const { data: subsData } = useQuery({
    queryKey: ['sub-accounts', 'mine', 'switcher'],
    queryFn: () => subAccountsAPI.getSubAccounts({ page: 1, limit: 200 }),
    enabled: open && !!profile,
    staleTime: 30_000,
  });

  const subs = (subsData?.rows ?? []) as SubRow[];
  const total = subsData?.total ?? subCount ?? subs.length;
  const title =
    total > 0
      ? t('accountSwitcher.titleWithCount', { count: total, defaultValue: `切换账户 · ${total} 个子账户` })
      : t('accountSwitcher.title', '切换账户');

  function pickMain() {
    setActiveAccount(null, null);
    onClose();
  }

  function pickSub(sub: SubRow) {
    setActiveAccount(sub.id, sub.username);
    onClose();
  }

  return (
    <ModalSheet open={open} onClose={onClose} title={title}>
      <div className="flex flex-col gap-2">
        <AccountOption
          label={`${profile?.username ?? '—'} (${t('accountSwitcher.main', '主账户')})`}
          sublabel={t('accountSwitcher.mainHint', '显示主账户 ORB / EP / SP 等资产')}
          active={activeId === null}
          onClick={pickMain}
        />
        {subs.map((sub) => {
          const bal = sub.balances ?? sub;
          const sublabel = `ORB ${fmt(bal.uo)} · EP1 ${fmt(bal.ep1)} · SP1 ${fmt(bal.sp1)} · ${sub.level_code ?? ''}`;
          return (
            <AccountOption
              key={sub.id}
              label={sub.username}
              sublabel={sublabel}
              active={activeId === sub.id}
              onClick={() => pickSub(sub)}
            />
          );
        })}
        {subs.length === 0 ? (
          <div className="rounded-xl border px-3 py-3 text-xs" style={{ borderColor: C.softBorder, color: C.muted }}>
            {t('accountSwitcher.noSubs', '暂无子账户')}
          </div>
        ) : null}
        {total > subs.length ? (
          <div className="px-1 text-[10px]" style={{ color: C.muted }}>
            {t('accountSwitcher.partialList', {
              shown: subs.length,
              total,
              defaultValue: `显示前 ${subs.length} 个，共 ${total} 个子账户`,
            })}
          </div>
        ) : null}
        {total > 0 ? (
          <button
            type="button"
            onClick={() => {
              onClose();
              navigate('/sub-accounts');
            }}
            className="mt-1 w-full rounded-xl border py-3 text-center text-sm font-semibold transition-all active:scale-[0.98]"
            style={{ borderColor: `${C.gold}66`, color: C.gold, background: C.chipBg }}
          >
            {t('accountSwitcher.viewAll', { count: total, defaultValue: `查看全部 ${total} 个子账户` })}
          </button>
        ) : null}
      </div>
    </ModalSheet>
  );
}

export function AccountSwitcherBar({
  onOpen,
  onViewAll,
  subCount = 0,
}: {
  onOpen: () => void;
  onViewAll?: () => void;
  subCount?: number;
}) {
  const { t } = useTranslation();
  const profile = useAuthStore((s) => s.profile);
  const activeId = useActiveAccountStore((s) => s.accountId);
  const activeUsername = useActiveAccountStore((s) => s.username);

  const displayName = activeId ? (activeUsername ?? `#${activeId}`) : (profile?.username ?? '—');
  const isSub = activeId !== null;

  return (
    <div
      className="flex w-full flex-col gap-2 rounded-xl border px-3 py-2.5"
      style={{ borderColor: `${C.gold}44`, background: C.inputBg }}
    >
      <button
        type="button"
        onClick={onOpen}
        className="flex w-full items-center justify-between text-left transition-all active:scale-[0.98]"
      >
        <div className="min-w-0">
          <div className="text-[10px]" style={{ color: C.muted }}>
            {t('accountSwitcher.viewing', '当前查看')}
          </div>
          <div className="truncate text-sm font-semibold" style={{ color: C.gold }}>
            {displayName}
            {isSub ? ` · ${t('accountSwitcher.subTag', '子账户')}` : ` · ${t('accountSwitcher.mainTag', '主账户')}`}
          </div>
        </div>
        <span className="text-xs font-semibold" style={{ color: C.gold }}>
          {t('accountSwitcher.switch', '切换')} ›
        </span>
      </button>
      {subCount > 0 ? (
        <div className="flex items-center justify-between gap-2 border-t pt-2" style={{ borderColor: `${C.gold}22` }}>
          <span className="text-xs" style={{ color: C.muted }}>
            {t('accountSwitcher.subCount', { count: subCount, defaultValue: `${subCount} 个子账户` })}
          </span>
          {onViewAll ? (
            <button
              type="button"
              onClick={onViewAll}
              className="text-xs font-semibold transition-all active:scale-[0.98]"
              style={{ color: C.gold }}
            >
              {t('accountSwitcher.viewAllShort', '查看全部')} ›
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
